import { conformingAttachmentCreation } from '@/pages/communicate/attachmentCreation';
import type { BundleAttachment, ParsedBundleContent } from '@/lib/medcom/parseCareCommunicationBundle';

export type ThreadLetterMessage = {
  id: string;
  direction: 'in' | 'out';
  sentAt?: string | null;
  receivedAt?: string | null;
  createdAt: string;
};

export type ThreadAttachment = {
  key: string;
  messageId: string;
  messageAt: string | null;
  senderName: string | null;
  title: string;
  identifier: string | null;
  contentType: string | null;
  data: string | null;
  url: string | null;
  creation: string | null;
  authoredAt: string | null;
  authorName: string | null;
  authorPhone: string | null;
};

export type CarriedAttachmentPayload = {
  kind: 'attachment';
  identifier?: string;
  authoredAt?: string;
  attachment: {
    contentType: string;
    title: string;
    data?: string;
    url?: string;
    creation?: string;
  };
};

/**
 * Attachments from letters in the thread, in message order.
 * The same identifier is listed once, from the earliest letter that carried it.
 */
export function threadAttachmentsFromLetters(
  messages: readonly ThreadLetterMessage[],
  letters: ReadonlyMap<string, ParsedBundleContent | null | undefined>,
): ThreadAttachment[] {
  const collected: ThreadAttachment[] = [];
  for (const message of messages) {
    const letter = letters.get(message.id);
    if (!letter) continue;
    const messageAt = letter.messageTimestamp ?? messageInstant(message);
    for (const attachment of letter.attachments) {
      const title = attachment.title?.trim() ?? '';
      if (!title && !attachment.segmentId && !attachment.data && !attachment.url) continue;
      collected.push(threadAttachment(message.id, messageAt, letter, attachment));
    }
  }
  return dedupeByOriginalIdentifier(collected);
}

/** Svar must not carry received attachments. Only Videresend may. */
export function mayCarryThreadAttachments(mode: 'new' | 'reply' | 'forward'): boolean {
  return mode === 'forward';
}

/**
 * Thread-picker rows that are not already ticked under Vedhæftninger, der følger med.
 */
export function pickerThreadAttachments(
  attachments: readonly ThreadAttachment[],
  alreadyForwarded: ReadonlySet<string>,
): ThreadAttachment[] {
  return attachments.filter((attachment) => {
    const key = identifierKey(attachment.identifier);
    return !key || !alreadyForwarded.has(key);
  });
}

export function newAttachmentIdentifier(
  randomUuid: () => string = () => crypto.randomUUID(),
): string {
  return `urn:uuid:${randomUuid()}`;
}

/** File name without the allowed extension, same rule as the compose form. */
export function attachmentTitleFromFileName(fileName: string): string {
  const lower = fileName.toLowerCase();
  const extensions = ['.jpeg', '.jpg', '.tiff', '.tif', '.png', '.gif', '.bmp', '.pdf'];
  const extension = extensions.find((item) => lower.endsWith(item));
  if (!extension) return fileName;
  const withoutExtension = fileName.slice(0, -extension.length);
  return withoutExtension || fileName;
}

/**
 * Payload copies of the ticked thread attachments.
 * A parent-letter part already sent as forwardSegmentIds is not copied again.
 * The same identifier is emitted at most once.
 */
export function carriedAttachmentPayloads(
  attachments: readonly ThreadAttachment[],
  selectedKeys: ReadonlySet<string>,
  alreadyForwarded: ReadonlySet<string>,
): CarriedAttachmentPayload[] {
  const used = new Set<string>();
  const result: CarriedAttachmentPayload[] = [];
  for (const attachment of attachments) {
    if (!selectedKeys.has(attachment.key)) continue;
    const key = identifierKey(attachment.identifier);
    if (key && alreadyForwarded.has(key)) continue;
    if (key) {
      if (used.has(key)) continue;
      used.add(key);
    }
    result.push(toPayload(attachment));
  }
  return result;
}

export function alreadyForwardedIdentifiers(input: {
  mode: 'new' | 'reply' | 'forward';
  quotedAttachments: ReadonlyArray<{ segmentId?: string | null }>;
  forwardSegmentIds: string[] | undefined;
}): Set<string> {
  const ids = new Set<string>();
  switch (input.mode) {
    case 'new':
    case 'reply':
      return ids;
    case 'forward': {
      if (input.forwardSegmentIds === undefined) {
        for (const attachment of input.quotedAttachments) {
          addIdentifier(ids, attachment.segmentId);
        }
        return ids;
      }
      for (const id of input.forwardSegmentIds) {
        if (input.quotedAttachments.some((attachment) => sameIdentifier(attachment.segmentId, id))) {
          addIdentifier(ids, id);
        }
      }
      return ids;
    }
    default: {
      const unexpected: never = input.mode;
      return unexpected;
    }
  }
}

function threadAttachment(
  messageId: string,
  messageAt: string | null,
  letter: ParsedBundleContent,
  attachment: BundleAttachment,
): ThreadAttachment {
  const senderName =
    letter.senderPractitionerName?.trim() || letter.specificSenderName?.trim() || null;
  return {
    key: `${messageId}:${attachment.segmentId ?? attachment.payloadIndex}`,
    messageId,
    messageAt,
    senderName,
    title: attachment.title?.trim() || '',
    identifier: attachment.segmentId,
    contentType: attachment.contentType,
    data: attachment.data,
    url: attachment.url,
    creation: attachment.creation,
    authoredAt: attachment.authoredAt,
    authorName: attachment.authorName,
    authorPhone: attachment.authorPhone,
  };
}

function dedupeByOriginalIdentifier(attachments: readonly ThreadAttachment[]): ThreadAttachment[] {
  const bestById = new Map<string, ThreadAttachment>();
  for (const attachment of attachments) {
    const key = identifierKey(attachment.identifier);
    if (!key) continue;
    const current = bestById.get(key);
    bestById.set(key, current ? preferOriginalSource(current, attachment) : attachment);
  }

  const seen = new Set<string>();
  const result: ThreadAttachment[] = [];
  for (const attachment of attachments) {
    const key = identifierKey(attachment.identifier);
    if (!key) {
      result.push(attachment);
      continue;
    }
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(bestById.get(key) ?? attachment);
  }
  return result;
}

function preferOriginalSource(
  current: ThreadAttachment,
  candidate: ThreadAttachment,
): ThreadAttachment {
  const currentTime = instantMs(current.messageAt);
  const candidateTime = instantMs(candidate.messageAt);
  const earlier =
    candidateTime != null && (currentTime == null || candidateTime < currentTime)
      ? candidate
      : current;
  const other = earlier === current ? candidate : current;
  if (earlier.data || !other.data) {
    if (earlier.url || !other.url) return earlier;
    return { ...earlier, url: other.url };
  }
  return { ...earlier, data: other.data, url: earlier.url ?? other.url };
}

function toPayload(attachment: ThreadAttachment): CarriedAttachmentPayload {
  const contentType = attachment.contentType?.trim() || 'application/octet-stream';
  const title = attachment.title || ' ';
  const creation = conformingAttachmentCreation(attachment.creation);
  const authoredAt =
    conformingAttachmentCreation(attachment.authoredAt) ??
    conformingAttachmentCreation(attachment.creation);
  const payload: CarriedAttachmentPayload = {
    kind: 'attachment',
    attachment: {
      contentType,
      title,
    },
  };
  if (attachment.identifier) payload.identifier = attachment.identifier;
  if (authoredAt) payload.authoredAt = authoredAt;
  if (attachment.data) payload.attachment.data = attachment.data;
  if (attachment.url) payload.attachment.url = attachment.url;
  if (creation) payload.attachment.creation = creation;
  return payload;
}

function messageInstant(message: ThreadLetterMessage): string | null {
  const value =
    message.direction === 'in'
      ? message.receivedAt ?? message.createdAt
      : message.sentAt ?? message.createdAt;
  return value?.trim() || null;
}

function identifierKey(value: string | null | undefined): string | null {
  const text = value?.trim() ?? '';
  if (!text) return null;
  return text.replace(/^urn:uuid:/i, '').toLowerCase();
}

function sameIdentifier(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const a = identifierKey(left);
  const b = identifierKey(right);
  return Boolean(a && b && a === b);
}

function addIdentifier(ids: Set<string>, value: string | null | undefined): void {
  const key = identifierKey(value);
  if (key) ids.add(key);
}

function instantMs(value: string | null | undefined): number | null {
  const text = value?.trim() ?? '';
  if (!text) return null;
  const time = Date.parse(text);
  return Number.isNaN(time) ? null : time;
}
